import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Building2, Shield } from "lucide-react";
import { superApi, PLANS } from "@/lib/superApi";

export default function SuperClient() {
  const [platform, setPlatform] = useState(null);
  const [planId, setPlanId] = useState("basico");
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingLicense, setSavingLicense] = useState(false);

  const load = () =>
    superApi.get("/super/platform").then((r) => {
      setPlatform(r.data);
      setPlanId(r.data?.plan_id || "basico");
    });

  useEffect(() => {
    load().catch(() => toast.error("No se pudo cargar la plataforma"));
  }, []);

  const loadPreview = async (pid) => {
    if (!platform || pid === platform.plan_id) {
      setPreview(null);
      return;
    }
    try {
      const { data } = await superApi.get(`/super/platform/plan-preview?plan_id=${pid}`);
      setPreview(data);
    } catch {
      setPreview(null);
    }
  };

  const onPlanSelect = (pid) => {
    setPlanId(pid);
    loadPreview(pid);
  };

  const applyPlan = async (confirmDowngrade = false) => {
    setSavingPlan(true);
    try {
      const { data } = await superApi.put("/super/platform/plan", {
        plan_id: planId,
        confirm_downgrade: confirmDowngrade,
      });
      setPlatform(data);
      setPreview(null);
      setConfirmOpen(false);
      toast.success("Plan actualizado");
      if (data.branches_removed) {
        toast.info(`Se eliminaron ${data.branches_removed} sucursal(es)`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.includes("confirm_downgrade")) {
        setConfirmOpen(true);
      } else {
        toast.error(detail || "Error al cambiar plan");
      }
    } finally {
      setSavingPlan(false);
    }
  };

  const toggleLicense = async () => {
    setSavingLicense(true);
    try {
      const { data } = await superApi.put("/super/platform/admin-license", {
        admin_licensed: !platform.admin_licensed,
      });
      setPlatform(data);
      toast.success(data.admin_licensed ? "Panel admin activado" : "Panel admin desactivado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSavingLicense(false);
    }
  };

  if (!platform) {
    return <div className="text-white/60">Cargando…</div>;
  }

  const quotaLabel =
    platform.max_branches == null
      ? `${platform.branch_count} sucursales — ${platform.plan_name}`
      : `${platform.branch_count} de ${platform.max_branches} sucursales — ${platform.plan_name}`;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#E63946]">Suscripción</div>
        <h1 className="font-display text-4xl text-white mt-1">Plan comercial</h1>
        <p className="text-white/60 text-sm mt-2">Básico, Pro o Elite — cupo de sucursales, módulos y licencia del panel admin.</p>
      </div>

      <div className="glass p-5 rounded-sm space-y-3">
        <div className="flex items-center gap-2 text-white">
          <Building2 size={18} className="text-[#FFD700]" />
          <span className="font-display text-xl">{platform.plan_name}</span>
          <span className="text-white/60 text-sm">— {platform.plan_tagline}</span>
        </div>
        <p className="text-sm text-white/60">{quotaLabel}</p>
        <div className="text-xs text-white/40 grid sm:grid-cols-2 gap-2">
          <span>Admins de sucursal: {platform.branch_admins_allowed ? "Permitidos" : "No permitidos"}</span>
          <span>Respaldo BD: {platform.backup_tier}</span>
        </div>
      </div>

      <div className="glass p-5 rounded-sm flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield className="text-[#E63946]" size={20} />
          <div>
            <div className="text-white font-medium">Panel de administración</div>
            <div className="text-xs text-white/60">
              {platform.admin_licensed ? "Licenciado — el maestro puede entrar" : "Desactivado — bloquea login admin"}
            </div>
          </div>
        </div>
        <button type="button" onClick={toggleLicense} disabled={savingLicense} className={platform.admin_licensed ? "btn-outline" : "btn-primary"}>
          {savingLicense ? <Loader2 className="animate-spin" size={14} /> : platform.admin_licensed ? "Desactivar" : "Activar"}
        </button>
      </div>

      <div className="glass p-5 rounded-sm space-y-4">
        <h2 className="font-display text-xl text-white">Cambiar plan</h2>
        <div className="grid gap-3">
          {PLANS.map((p) => (
            <label
              key={p.id}
              className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer ${
                planId === p.id ? "border-[#FFD700]/60 bg-[#FFD700]/5" : "border-white/10 hover:border-white/20"
              } ${platform.plan_id === p.id ? "ring-1 ring-green-500/30" : ""}`}
            >
              <input type="radio" className="mt-1" checked={planId === p.id} onChange={() => onPlanSelect(p.id)} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{p.name}</span>
                  {platform.plan_id === p.id && (
                    <span className="text-[10px] uppercase tracking-widest text-green-400 border border-green-500/40 px-1.5 py-0.5 rounded-sm">Actual</span>
                  )}
                </div>
                <div className="text-xs text-[#FFD700]/80">{p.tagline}</div>
                <div className="text-xs text-white/60 mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1">
                  <span>Sucursales: {p.branches}</span>
                  <span>Admins locales: {p.branchAdmins}</span>
                  <span>Asistencia: {p.attendance}</span>
                  <span>Pagos MP: {p.payments}</span>
                  <span>Respaldo BD: {p.backup}</span>
                </div>
              </div>
            </label>
          ))}
        </div>
        {preview?.requires_confirm_downgrade && (
          <div className="text-sm text-amber-200/90 bg-amber-500/10 border border-amber-500/30 p-3 rounded-sm">
            Se eliminarán {preview.branches_to_remove?.length} sucursal(es).
            {preview.subscribers_to_migrate > 0 && (
              <> {preview.subscribers_to_migrate} suscriptor(es) vigente(s) pasarán a la matriz.</>
            )}
          </div>
        )}
        <button
          type="button"
          disabled={savingPlan || planId === platform.plan_id}
          onClick={() => (preview?.requires_confirm_downgrade ? setConfirmOpen(true) : applyPlan(false))}
          className="btn-primary"
        >
          {savingPlan ? <Loader2 className="animate-spin" size={14} /> : "Aplicar plan"}
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="glass-strong max-w-md w-full p-6 rounded-sm space-y-4">
            <h3 className="font-display text-2xl text-white">Confirmar downgrade</h3>
            <p className="text-sm text-white/60">
              Esta acción eliminará sucursales y datos asociados. Los suscriptores vigentes se migrarán a la matriz.
            </p>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn-outline" onClick={() => setConfirmOpen(false)}>Cancelar</button>
              <button type="button" className="btn-primary bg-red-700 hover:bg-red-600" onClick={() => applyPlan(true)} disabled={savingPlan}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
